import React, { useState, useEffect } from 'react';
import { User, Calendar, FileText, Edit, Archive, UserPlus } from 'lucide-react';
import { CURRENCIES, LEASE_STATUS } from '../../utils/constants';
import { getPropertyDocumentUrl } from '../../services/supabase/propertyStorage';
import TextInputDialog from '../ui/TextInputDialog';
import ConfirmDialog from '../ui/ConfirmDialog';

// Komponent przycisku podglądu dokumentu - używa <a> zamiast window.open
function DocumentPreviewButton({ contractPath }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClick = async (e) => {
    if (url) return; // Jeśli już mamy URL, pozwól na otwarcie

    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      console.log('[DocumentPreviewButton] Fetching URL for:', contractPath);
      const signedUrl = await getPropertyDocumentUrl(contractPath);

      if (!signedUrl) {
        throw new Error('Failed to get document URL');
      }

      console.log('[DocumentPreviewButton] Got URL, updating href');
      setUrl(signedUrl);

      // Automatycznie kliknij ponownie po uzyskaniu URL
      setTimeout(() => {
        const link = document.getElementById(`doc-link-${contractPath}`);
        if (link) link.click();
      }, 100);
    } catch (err) {
      console.error('[DocumentPreviewButton] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Resetuj URL po 5 minutach (przed wygaśnięciem signed URL)
  useEffect(() => {
    if (url) {
      const timer = setTimeout(() => {
        console.log('[DocumentPreviewButton] URL expired, resetting');
        setUrl(null);
      }, 5 * 60 * 1000); // 5 minut

      return () => clearTimeout(timer);
    }
  }, [url]);

  if (url) {
    // Jeśli mamy URL, renderuj normalny link
    return (
      <a
        id={`doc-link-${contractPath}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="px-4 py-2 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
      >
        <FileText className="w-4 h-4" />
        Podgląd umowy
      </a>
    );
  }

  // Jeśli nie mamy URL, renderuj przycisk który go pobierze
  return (
    <div className="flex flex-col gap-2">
      <button
        onClick={handleClick}
        disabled={loading}
        className={`px-4 py-2 text-sm border rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
          error
            ? 'border-red-300 text-red-700 hover:bg-red-50'
            : 'border-slate-300 text-slate-700 hover:bg-slate-50'
        }`}
      >
        <FileText className="w-4 h-4" />
        {loading ? 'Ładowanie...' : error ? 'Spróbuj ponownie' : 'Podgląd umowy'}
      </button>
      {error && (
        <p className="text-xs text-red-600">
          Błąd: {error}
        </p>
      )}
    </div>
  );
}

export default function LeaseCard({ lease, isActive, onTerminate, onEdit, onArchive, onInviteTenant }) {
  const [showTerminateDialog, setShowTerminateDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  const currency = CURRENCIES[lease.rental_currency] || CURRENCIES.PLN;
  const status = LEASE_STATUS[lease.status] || LEASE_STATUS.ended;

  return (
    <div className={`border rounded-xl p-5 ${isActive ? 'border-slate-300 bg-white shadow-md' : 'border-slate-200 bg-white'}`}>
      <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3">
        <div className="flex-1 w-full sm:w-auto">
          {/* Najemca */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              <User className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 text-lg">{lease.tenant_name}</h4>
              {lease.tenant_email && (
                <p className="text-sm text-slate-500">{lease.tenant_email}</p>
              )}
            </div>
          </div>

          {/* Daty i kwota */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-3">
            <div className="flex items-center justify-center sm:justify-start gap-2 text-sm">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">
                {new Date(lease.start_date).toLocaleDateString('pl-PL')}
              </span>
              {lease.end_date && (
                <>
                  <span className="text-slate-400">→</span>
                  <span className="text-slate-600">
                    {new Date(lease.end_date).toLocaleDateString('pl-PL')}
                  </span>
                </>
              )}
              {!lease.end_date && (
                <span className="text-slate-500 italic">(nieokreślona)</span>
              )}
            </div>
            <div className="flex items-center justify-center sm:justify-start gap-2 text-sm">
              <span className="font-medium text-slate-800">
                {lease.rental_amount} {currency.symbol}
              </span>
              <span className="text-slate-500">/mies.</span>
            </div>
          </div>

          {/* Dodatkowe informacje */}
          {(lease.payment_day || lease.deposit_amount) && (
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 md:gap-4 text-xs text-slate-600 mb-3">
              {lease.payment_day && (
                <span>Płatność: {lease.payment_day}. dnia miesiąca</span>
              )}
              {lease.deposit_amount && (
                <span>Kaucja: {lease.deposit_amount} {currency.symbol}</span>
              )}
            </div>
          )}

          {/* Notatki */}
          {lease.notes && (
            <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg mb-3">
              {lease.notes}
            </p>
          )}

          {/* Powód rozwiązania */}
          {lease.status === 'terminated' && lease.termination_reason && (
            <div className="text-sm bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
              <p className="text-red-700 font-medium mb-1">Powód rozwiązania:</p>
              <p className="text-red-600">{lease.termination_reason}</p>
              {lease.termination_date && (
                <p className="text-red-500 text-xs mt-1">
                  Data: {new Date(lease.termination_date).toLocaleDateString('pl-PL')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Status */}
        <div className="flex justify-center sm:justify-start sm:ml-2 w-full sm:w-auto">
          <span
            className="px-3 py-1 rounded-full text-xs font-medium text-white backdrop-blur-sm"
            style={{ backgroundColor: status.color + 'CC' }}
          >
            {status.label}
          </span>
        </div>
      </div>

      {/* Akcje */}
      <div className="mt-4 pt-4 border-t border-slate-200">
        <div className="flex gap-2 flex-wrap justify-center sm:justify-between">
          {/* Left side - action buttons */}
          <div className="flex gap-2 flex-wrap justify-center sm:justify-start">
            {lease.status !== 'archived' && (
              <button
                onClick={() => onEdit(lease)}
                className="px-4 py-2 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edytuj
              </button>
            )}
            {lease.contract_attachment_path && (
              <DocumentPreviewButton contractPath={lease.contract_attachment_path} />
            )}
            {isActive && onInviteTenant && (
              <button
                onClick={onInviteTenant}
                className="px-4 py-2 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Zaproś najemcę
              </button>
            )}
            {!isActive && lease.status !== 'archived' && (
              <button
                onClick={() => setShowArchiveDialog(true)}
                className="px-4 py-2 text-sm border border-slate-400 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <Archive className="w-4 h-4" />
                Archiwizuj
              </button>
            )}
          </div>

          {/* Right side - terminate button */}
          {isActive && (
            <button
              onClick={() => setShowTerminateDialog(true)}
              className="px-4 py-2 text-sm text-rose-600 rounded-lg hover:bg-rose-50 transition-colors"
            >
              Rozwiąż umowę
            </button>
          )}
        </div>
      </div>

      {/* Terminate Dialog */}
      <TextInputDialog
        isOpen={showTerminateDialog}
        title="Rozwiąż umowę"
        message="Podaj powód rozwiązania umowy:"
        placeholder="np. Zakończenie najmu, umowa zastępcza..."
        confirmText="Rozwiąż"
        cancelText="Anuluj"
        variant="danger"
        onConfirm={(reason) => {
          setShowTerminateDialog(false);
          onTerminate(reason, new Date().toISOString().split('T')[0]);
        }}
        onCancel={() => setShowTerminateDialog(false)}
      />

      {/* Archive Dialog */}
      <ConfirmDialog
        isOpen={showArchiveDialog}
        title="Archiwizuj umowę"
        message="Czy na pewno chcesz zarchiwizować tę umowę? Będzie ukryta na liście."
        confirmText="Archiwizuj"
        cancelText="Anuluj"
        variant="warning"
        onConfirm={() => {
          setShowArchiveDialog(false);
          onArchive();
        }}
        onCancel={() => setShowArchiveDialog(false)}
      />
    </div>
  );
}
